import { useTranslation } from 'react-i18next';
import { Button, Card, Skeleton, Tag, Typography, Flex, Form } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ContentCustom, PageHeaderCustom } from '../../../components';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useErrorMessage } from '../../../utils/errorMessage';
import { useSupervisionCollapseInfos } from './useSupervisionCollapseInfos';
import { ImagesLayout } from './ImagesLayout';
import { dateFormat } from './utils/dateFormat';
import { PensionersProfiles } from './pensionersProfiles/PensionersProfiles';
import { ReviewDrawer } from '../../../components/Review/ReviewDrawer';
import { ListDocuments } from './documents/ListDocuments';
import { getSupervision } from './utils/getSupervision';
import { ListReviews } from './reviews/ListReviews';
import { OwnerProfile } from './ownerProfile/OwnerProfile';
import { getReviews } from './utils/getReviews';
import { buttonActions } from './utils/buttonActions';
import { useSocketContext } from '../../../contexts/SocketContext';
import { CardTitle } from '../../../components/CardTitle/CardTitle';
import { InfoGrid } from '../../../components/InfoGrid/InfoGrid';
import { SupervisionStatus } from '../../../utils/constants/tagColors';

export const ShowMySupervision = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { message } = useErrorMessage();
  const { dispatchAPI, user } = useAuthContext();
  const [form] = Form.useForm();
  const { editSupervisionLock, onProfileLock } = useSocketContext();

  const [supervision, setSupervision] = useState({});
  const [veterinarian, setVeterinarian] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [refreshData, setRefreshData] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [anounce, setAnounce] = useState({});
  const [userAlreadySendReview, setUserAlreadySendReview] = useState(false);
  const [showReviewDrawer, setShowReviewDrawer] = useState(false);
  const [editReviewDrawer, setEditReviewDrawer] = useState(false);

  const isOwner = user?.role === 'users:OWNER';
  const userIsReviewAuthor = reviews.some(
    (review) => review.author.id.user === user?._id && review.status === 'SENT'
  );

  useEffect(() => {
    if (forceRefresh) {
      (async () => {
        await getReviews({
          dispatchAPI,
          supervision,
          setReviews,
          user,
          setUserAlreadySendReview,
          form,
          message
        });
        setForceRefresh(false);
      })();
    }
  }, [forceRefresh]);

  useEffect(() => {
    if (supervision?._id) {
      (async () => {
        await getReviews({
          dispatchAPI,
          supervision,
          setReviews,
          user,
          setUserAlreadySendReview,
          form,
          message
        });
      })();
    }
  }, [supervision]);

  const anouncePhotos = supervision?.summary?.anounce_photos || [];
  const title = t(`supervisions.users_shows.supervision_date`, {
    reference: supervision?.reference,
    ...dateFormat(
      supervision?.main_informations?.start_date,
      supervision?.main_informations?.end_date,
      user?.role
    )
  });

  const displayDocumentsCondition = ['BOOKED', 'IN_PROGRESS', 'FINISHED'];
  const displayPensionersProfilesCondition = [
    ...displayDocumentsCondition,
    'WAITING_BOOKING',
    'FINISHED'
  ];

  useEffect(() => {
    (async () => {
      await getSupervision({
        dispatchAPI,
        id,
        setSupervision,
        setVeterinarian,
        message,
        setIsLoading,
        setAnounce
      });
    })();
  }, [refreshData]);

  useEffect(() => {
    (async () => {
      await onProfileLock();
    })();
  }, []);

  const actionByStatus = buttonActions({
    navigate,
    supervision,
    dispatchAPI,
    message,
    setSupervision,
    setIsLoading,
    id,
    setOpen,
    t,
    editSupervisionLock
  })[supervision?.status];

  return (
    <>
      {isLoading ? (
        <Skeleton.Button
          loading={isLoading}
          active
          block
          style={{ margin: '20px 0 10px' }}
        />
      ) : (
        <PageHeaderCustom
          withoutBreadCrumb
          onBack="/supervisions"
          title={t('owners.users_shows.title.supervision_details', supervision)}
          extra={
            <Flex wrap gap={8} align="center">
              {supervision?.status === 'REQUEST' && (
                <Tag color="orange">
                  {t('supervisions.show.waiting_validation')}
                </Tag>
              )}
              {(!userIsReviewAuthor &&
                isOwner &&
                actionByStatus &&
                !userAlreadySendReview) ||
              (!userIsReviewAuthor &&
                !isOwner &&
                supervision.status === 'FINISHED' &&
                !userAlreadySendReview) ? (
                <Button type="primary" onClick={actionByStatus}>
                  {t(`owners.users_shows.buttons.${supervision?.status}`)}
                </Button>
              ) : null}
            </Flex>
          }
        />
      )}
      <ContentCustom transparent style={{ gap: 24 }}>
        <Skeleton
          loading={isLoading}
          active
          avatar={{ shape: 'square', size: 400 }}
        >
          <Card>
            <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {title}
              </Typography.Title>
              <Tag color={SupervisionStatus[supervision?.status]}>
                {t(`supervisions.tags.${supervision?.status}`)}
              </Tag>
            </Flex>
            {anouncePhotos.length ? (
              <div style={{ marginTop: 20 }}>
                <ImagesLayout url={anouncePhotos} />
              </div>
            ) : null}
          </Card>
          <Card
            title={
              <CardTitle icon="calendar">
                {t('supervisions.show.anounce.supervision_information')}
              </CardTitle>
            }
          >
            <InfoGrid
              items={useSupervisionCollapseInfos(
                supervision,
                veterinarian,
                t,
                user,
                anounce
              ).map(({ label, content, extra, newRow }, index) => ({
                label,
                content,
                extra,
                newRow,
                // La description de l'annonce occupe toute la largeur
                full: index === 0
              }))}
            />
          </Card>
          <Flex vertical gap="large">
            {displayPensionersProfilesCondition.includes(supervision?.status) &&
              isOwner && (
                <Card>
                  <PensionersProfiles supervision={supervision} />
                </Card>
              )}
            {!isOwner && (
              <Card>
                <OwnerProfile supervision={supervision} />
              </Card>
            )}
            {displayDocumentsCondition.includes(supervision?.status) && (
              <Card>
                <ListDocuments
                  supervision={supervision}
                  t={t}
                  refreshData={refreshData}
                  setRefreshData={setRefreshData}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />
              </Card>
            )}
            {reviews.length > 0 &&
              supervision?.status === 'FINISHED' &&
              userIsReviewAuthor && (
                <Card>
                  <ListReviews
                    reviews={reviews}
                    user={user}
                    t={t}
                    setShowReviewDrawer={setShowReviewDrawer}
                    setOpen={setOpen}
                    title={title}
                    setEditReviewDrawer={setEditReviewDrawer}
                  />
                </Card>
              )}
          </Flex>
        </Skeleton>
        <ReviewDrawer
          t={t}
          open={open}
          setOpen={setOpen}
          supervision={supervision}
          review={reviews}
          setReview={setReviews}
          setForceRefresh={setForceRefresh}
          showReviewDrawer={showReviewDrawer}
          customInstanceForm={form}
          questionnaireType={user?.role}
          editReviewDrawer={editReviewDrawer}
          reviewData={reviews.find(
            (review) =>
              review.author.id.user === user?._id && review.status === 'SENT'
          )}
        />
      </ContentCustom>
    </>
  );
};
